import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Video, Grid, Sparkles, ArrowRight, LogIn, ChevronDown, Play, Pause, Flame, Rocket, ChevronLeft, ChevronRight, Check } from 'lucide-react'

import logoIcon from '@/assets/icons/vs_64.png'

const StartPage: React.FC = () => {
  const navigate = useNavigate()
  const [isScrolled, setIsScrolled] = useState(false)
  const [activeFaq, setActiveFaq] = useState<number | null>(null)
  const [playingVideos, setPlayingVideos] = useState<Set<number>>(new Set())
  const [currentSlide, setCurrentSlide] = useState(1)
  const [activeProcessTab, setActiveProcessTab] = useState<'video' | 'canvas'>('video')
  const carouselRef = useRef<HTMLDivElement>(null)

  // Auto carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev === 0 ? 1 : 0))
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // SEO: Update meta tags
  useEffect(() => {
    document.title = 'VisSynth - AI Video Creation & Workflow Editor | Start Creating'

    const updateOrCreateMeta = (attrName: string, attrValue: string, content: string) => {
      let meta = document.querySelector(`meta[${attrName}="${attrValue}"]`)
      if (!meta) {
        meta = document.createElement('meta')
        meta.setAttribute(attrName, attrValue)
        document.head.appendChild(meta)
      }
      meta.setAttribute('content', content)
    }

    updateOrCreateMeta('name', 'description', 'Start creating with VisSynth - choose between AI video creation or canvas workflow editor. Create professional videos or build complex AI workflows with our intuitive platform.')
    updateOrCreateMeta('name', 'keywords', 'AI video generator, canvas workflow editor, AI workflow builder, visual AI programming, text to video, AI content creation platform')
    updateOrCreateMeta('property', 'og:title', 'VisSynth - AI Video Creation & Workflow Editor')
    updateOrCreateMeta('property', 'og:description', 'Choose between AI video creation or canvas workflow editor. Start creating professional content with VisSynth.')
    updateOrCreateMeta('property', 'og:type', 'website')
    updateOrCreateMeta('property', 'og:url', 'https://vissynth.com/start')
    updateOrCreateMeta('property', 'og:image', 'https://vissynth.com/og-image.svg')
    updateOrCreateMeta('property', 'og:image:width', '1200')
    updateOrCreateMeta('property', 'og:image:height', '630')
    updateOrCreateMeta('property', 'og:image:alt', 'VisSynth AI Creation Platform')
    updateOrCreateMeta('property', 'og:image:type', 'image/svg+xml')
    updateOrCreateMeta('name', 'twitter:card', 'summary_large_image')
    updateOrCreateMeta('name', 'twitter:title', 'VisSynth - AI Video Creation & Workflow Editor')
    updateOrCreateMeta('name', 'twitter:description', 'Start creating with VisSynth - choose between AI video creation or canvas workflow editor.')
    updateOrCreateMeta('name', 'twitter:image', 'https://vissynth.com/og-image.svg')

    let canonical = document.querySelector('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', 'https://vissynth.com/start')

    updateOrCreateMeta('name', 'author', 'VisSynth')
    updateOrCreateMeta('name', 'robots', 'index, follow')

    // Additional SEO tags
    updateOrCreateMeta('name', 'mobile-web-app-capable', 'yes')
    updateOrCreateMeta('name', 'apple-mobile-web-app-capable', 'yes')
    updateOrCreateMeta('name', 'apple-mobile-web-app-status-bar-style', 'black-translucent')
    updateOrCreateMeta('name', 'application-name', 'VisSynth')

    // Structured data
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'VisSynth',
      url: 'https://vissynth.com/start',
      applicationCategory: 'MultimediaApplication',
      operatingSystem: 'Web',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.8',
        ratingCount: '1200'
      },
      description: 'AI-powered platform for video creation and workflow editing',
      keywords: 'AI video generator, canvas workflow editor, AI workflow builder, visual AI programming',
      browserRequirements: 'Requires JavaScript. Requires HTML5.',
      softwareVersion: '1.0',
      author: {
        '@type': 'Organization',
        name: 'VisSynth',
        url: 'https://vissynth.com'
      },
      featureList: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@type': 'Thing',
            name: 'AI Video Creation',
            description: 'Transform text into professional videos with AI'
          }
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@type': 'Thing',
            name: 'Canvas Workflow Editor',
            description: 'Build complex AI workflows with visual programming'
          }
        }
      ]
    }

    let schemaScript = document.getElementById('structured-data')
    if (!schemaScript) {
      schemaScript = document.createElement('script')
      schemaScript.setAttribute('id', 'structured-data')
      schemaScript.setAttribute('type', 'application/ld+json')
      document.head.appendChild(schemaScript)
    }
    schemaScript.textContent = JSON.stringify(schema)

    return () => {
      document.querySelectorAll('meta[name="description"], meta[name="keywords"], link[rel="canonical"], #structured-data, meta[property^="og:"], meta[name^="twitter:"]').forEach(el => el.remove())
    }
  }, [])

  const features = [
    {
      id: 'video-feature-1',
      icon: Video,
      title: 'AI Video Generation',
      description: 'Transform text prompts into stunning videos using AI-powered image generation',
      gradient: 'from-cyan-500 via-blue-500 to-purple-500',
      glow: 'shadow-cyan-500/50',
      category: 'video'
    },
    {
      id: 'canvas-feature-1',
      icon: Grid,
      title: 'Node-based Editor',
      description: 'Build complex AI workflows using drag-and-drop nodes',
      gradient: 'from-purple-500 via-pink-500 to-rose-500',
      glow: 'shadow-purple-500/50',
      category: 'canvas'
    },
    {
      id: 'video-feature-2',
      icon: Sparkles,
      title: 'Smart Storyboarding',
      description: 'AI automatically generates engaging storyboards and transitions',
      gradient: 'from-pink-500 via-rose-500 to-red-500',
      glow: 'shadow-pink-500/50',
      category: 'video'
    },
    {
      id: 'canvas-feature-2',
      icon: Rocket,
      title: 'Workflow Templates',
      description: 'Quickly start with preset templates and save time',
      gradient: 'from-emerald-500 via-green-500 to-lime-500',
      glow: 'shadow-emerald-500/50',
      category: 'canvas'
    }
  ]

  const exampleVideos = [
    {
      title: 'Solar System Exploration',
      category: 'Education',
      videoUrl: 'https://data.vissynth.com/video/QCPxqOd9C4BJm4N1/6sjTOmJxI6FDQ2Xf/video_task_1766655496917252748_U29sYXIgU3lzdGVtIEV4cGxvcmF0aW9uIFN0b3J5Ym9hcmQ=_1766655596.mp4',
      posterUrl: '/example-video-1.png',
      gradient: 'from-blue-600 via-cyan-500 to-teal-400'
    },
    {
      title: 'Harmony Smart Home',
      category: 'Technology',
      videoUrl: 'https://data.vissynth.com/video/RNuCN9aupLICDvAo/AMLZYoP1Ca1XsMzJ/video_task_1766657185809179921_SGFybW9ueSBTbWFydCBIb21lIEV4cGVyaWVuY2UgU3Rvcnlib2FyZA==_1766657274.mp4',
      posterUrl: '/example-video-2.png',
      gradient: 'from-purple-600 via-pink-500 to-rose-400'
    },
    {
      title: 'Enchanted Forest',
      category: 'Nature',
      videoUrl: 'https://data.vissynth.com/video/DjCcLNoeBPKE9XSg/Ogh5g63HGupdiCxZ/video_task_1766656134557276202_RW5jaGFudGVkIEZvcmVzdCBTeW1waG9ueSBTdG9yeWJvYXJk_1766656257.mp4',
      posterUrl: '/example-video-3.png',
      gradient: 'from-emerald-600 via-green-500 to-lime-400'
    },
    {
      title: 'City Superhero Delivery',
      category: 'Entertainment',
      videoUrl: 'https://data.vissynth.com/video/WGJTm09biKR54K1z/dHSKdHyw99OWzD4w/video_task_1766656957552081268_Q2l0eSBTdXBlcmhlcm8gRGVsaXZlcnkgU2VydmljZSBTdG9yeWJvYXJk_1766657061.mp4',
      posterUrl: '/example-video-4.png',
      gradient: 'from-amber-600 via-orange-500 to-yellow-400'
    }
  ]

  const steps = [
    {
      number: 1,
      title: 'Choose Creation Method',
      description: 'Select video creation or canvas workflow based on your needs',
      color: 'blue'
    },
    {
      number: 2,
      title: 'Set Parameters',
      description: 'Configure the appropriate settings and options for your selected tool',
      color: 'purple'
    },
    {
      number: 3,
      title: 'AI Generation',
      description: 'Our AI system processes your request and generates the corresponding content',
      color: 'pink'
    },
    {
      number: 4,
      title: 'Export and Share',
      description: 'Download your creation and share it on social media platforms',
      color: 'cyan'
    }
  ]

  const canvasSteps = [
    {
      number: 1,
      title: 'Open Flow Canvas',
      description: 'Launch the node-based editor and start building your workflow',
      color: 'purple'
    },
    {
      number: 2,
      title: 'Add and Connect Nodes',
      description: 'Drag and drop nodes, then connect them to create your AI pipeline',
      color: 'pink'
    },
    {
      number: 3,
      title: 'Configure Node Parameters',
      description: 'Set up each node with the appropriate settings and inputs',
      color: 'rose'
    },
    {
      number: 4,
      title: 'Execute and Export',
      description: 'Run your workflow and export the generated content',
      color: 'purple'
    }
  ]

  const faqs = [{
    question: 'What features does VisSynth offer?',
    answer: 'VisSynth offers two core features: AI video generation (transforming text into professional videos) and canvas workflow editor (building complex AI workflows through visual programming).'
  },
  {
    question: 'Do I need programming skills to use the canvas workflow editor?',
    answer: 'No! The canvas workflow editor is designed to be intuitive and easy to use, with drag-and-drop functionality and preset templates to help you get started quickly, no coding skills required.'
  },
  {
    question: 'How does AI video generation work?',
    answer: 'VisSynth uses advanced AI models for scripts, images, and specialized video generation models. Simply describe your vision, and our AI handles everything.'
  },
  {
    question: 'Do I need video editing experience?',
    answer: 'Not at all! VisSynth is designed for everyone. Our AI automates complex video editing tasks. No technical skills or camera equipment required.'
  },
  {
    question: 'What video formats are supported?',
    answer: 'We export videos optimized for all major platforms: TikTok (9:16), YouTube Shorts (9:16), Instagram Reels (9:16), and standard formats (16:9). Export in MP4 format.'
  },
  {
    question: 'Can generated videos be used commercially?',
    answer: 'Yes! All videos you create with VisSynth can be used commercially. Perfect for content creators, marketers, and businesses.'
  },
  {
    question: 'How long does video generation take?',
    answer: 'Most videos are generated within 2-5 minutes, depending on length and complexity. Short videos (15-30 seconds) can be completed in less than 2 minutes.'
  },
  {
    question: 'Is there a free trial?',
    answer: 'Yes! Get started with our free plan to explore all features. Upgrade to Pro for unlimited generations and advanced features.'
  }
  ]

  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index)
  }

  return (
    <div className="bg-gradient-to-b from-gray-950 via-slate-950 via-gray-900 via-slate-900 to-gray-950 text-white min-h-screen overflow-x-hidden">
      {/* Floating Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-2xl border-b border-white/5 transition-all duration-300 ${isScrolled
        ? 'bg-gray-950/50 py-2 shadow-lg'
        : 'bg-gray-950/10 py-4'
        }`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            {/* Left: Logo and Brand */}
            <div className="flex items-center space-x-1 cursor-pointer" onClick={() => navigate('/')}>
              <img src={logoIcon} alt="V" className="w-8 h-8 self-center" />
              <span className="text-xl font-black text-white self-center mt-0.5">
                VisSynth
              </span>
            </div>

            {/* Center: Navigation Links */}
            <nav className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => navigate('/pricing')}
                className="text-gray-300 hover:text-white transition-colors font-medium text-sm"
              >
                Pricing
              </button>
              <button
                onClick={() => navigate('/feedback')}
                className="text-gray-300 hover:text-white transition-colors font-medium text-sm"
              >
                Help
              </button>
            </nav>

            {/* Right: Login and Start Creating */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/login')}
                className="hidden sm:flex items-center space-x-2 px-4 py-2 text-gray-300 hover:text-white transition-colors font-medium text-sm"
              >
                <LogIn className="w-4 h-4" />
                <span>Login</span>
              </button>
              {/* Start Creating button hidden */}
            </div>
          </div>
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-16"></div>

      {/* Animated Background Particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="particle absolute w-1 h-1 bg-white/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 15}s`,
              animationDuration: `${15 + Math.random() * 10}s`
            }}
          />
        ))}
      </div>

      {/* Hero Section - Combined Introduction and Carousel */}
      <section className="relative overflow-hidden" ref={carouselRef}>
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px]"></div>

        {/* Animated gradient background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-r from-cyan-500/30 via-blue-500/30 to-purple-500/30 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-r from-purple-500/30 via-pink-500/30 to-rose-500/30 rounded-full blur-3xl animate-float-delayed"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20 rounded-full blur-3xl animate-pulse"></div>
        </div>
        {/* Introduction Section */}
        <div className="relative max-w-7xl mx-auto px-6 pt-16 text-center">
          <h1 className="text-5xl lg:text-7xl font-bold leading-tight mb-6">
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 animate-gradient-flow font-black">
              Welcome to VisSynth
            </span>
          </h1>
          <p className="text-2xl lg:text-3xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
            Revolutionize your creative workflow with AI-powered visual synthesis tools
          </p>
        </div>
        {/* Carousel Container */}
        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-20 mb-8">
          {/* Carousel Slides */}
          <div className="relative overflow-hidden">
            {/* Slide 1: Video Creation */}
            <div className={`transition-all duration-1000 ease-in-out transform ${currentSlide === 0 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full absolute inset-0'}`}>
              <div className="grid lg:grid-cols-[1fr,1.1fr] gap-8 items-center px-4 sm:px-6">
                {/* Left: Content */}
                <div className="space-y-8 animate-slide-in-up min-h-[400px] flex flex-col justify-center">
                  {/* Badge */}
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md overflow-hidden relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <Sparkles className="w-4 h-4 text-cyan-400 relative z-10" />
                    <span className="text-sm text-cyan-300 font-medium relative z-10">✨ AI Video Creation</span>
                  </div>

                  {/* Main Heading */}
                  <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                    <span className="block mb-2">Instant Reel - </span>
                    <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 animate-gradient-flow font-black">
                      Create Viral Videos With AI Magic
                    </span>
                  </h1>

                  {/* Subheading */}
                  <p className="text-xl text-gray-300 leading-relaxed max-w-xl">
                    Transform your ideas into stunning videos in seconds. No camera, no editing skills required.
                    <span className="block mt-2 text-cyan-400 font-semibold">Perfect for TikTok, YouTube & Instagram</span>
                  </p>

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={() => navigate('/production/video/input')}
                      className="group relative px-8 py-4 bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 rounded-lg font-bold text-lg neon-glow text-white overflow-hidden transition-all duration-300 hover:scale-105 animate-gradient-flow"
                    >
                      <span className="relative z-10 flex items-center justify-center gap-2">
                        Start Creating Free
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                      </span>
                    </button>
                    <button
                      onClick={() => document.getElementById('example-videos')?.scrollIntoView({ behavior: 'smooth' })}
                      className="px-8 py-4 border-2 border-purple-500/50 hover:border-purple-400 rounded-lg font-semibold transition-all duration-300 hover:bg-purple-500/10 flex items-center justify-center gap-2 backdrop-blur-sm"
                    >
                      <Play className="w-5 h-5" />
                      Watch Demo
                    </button>
                  </div>

                  {/* Trust badges */}
                  <div className="flex flex-wrap gap-6 text-sm text-gray-400">
                    {['150 credits free', 'Cancel anytime'].map((badge, i) => (
                      <div key={i} className="flex items-center gap-2 animate-bounce-in" style={{ animationDelay: `${i * 0.1}s` }}>
                        <Check className="w-4 h-4 text-emerald-400" />
                        <span>{badge}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right: Video Demo */}
                <div className="relative animate-float">
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-purple-500/20 border border-purple-500/30 group">
                    {/* Video */}
                    <video
                      src="https://data.vissynth.com/video/MU7DySdnaqbnkUJx/oElh0yJ1yw9JUu1p/tutorial.mp4"
                      poster="/tutorial-preview.jpg"
                      className="w-full h-[400px] object-contain rounded-3xl transition-transform duration-700 group-hover:scale-105"
                      loop
                      playsInline
                      preload="metadata"
                      aria-label="Video Creation Demo"
                      onMouseEnter={(e) => {
                        const video = e.currentTarget;
                        video.play().catch(err => {
                          console.log('Video play error:', err);
                        });
                      }}
                      onMouseLeave={(e) => e.currentTarget.pause()}
                    />
                    {/* Overlay gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Slide 2: Canvas Workflow */}
            <div className={`transition-all duration-1000 ease-in-out transform ${currentSlide === 1 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full absolute inset-0'}`}>
              <div className="grid lg:grid-cols-[1fr,1.1fr] gap-8 items-center px-4 sm:px-6">
                {/* Left: Content */}
                <div className="space-y-8 animate-slide-in-up min-h-[400px] flex flex-col justify-center">
                  {/* Badge */}
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md overflow-hidden relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-rose-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <Grid className="w-4 h-4 text-purple-400 relative z-10" />
                    <span className="text-sm text-purple-300 font-medium relative z-10">✨ Canvas Workflow</span>
                  </div>

                  {/* Main Heading */}
                  <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                    <span className="block mb-2">Flow Canvas - </span>
                    <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-500 to-rose-600 animate-gradient-flow font-black">
                      Build AI Workflows With Nodes
                    </span>
                  </h1>

                  {/* Subheading */}
                  <p className="text-xl text-gray-300 leading-relaxed max-w-xl">
                    Create AI pipelines with a visual node-based editor. Drag, drop, and connect to unlock endless possibilities.
                    <span className="block mt-2 text-purple-400 font-semibold">Supports text, image, and video generation</span>
                  </p>

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={() => navigate('/production/canvas')}
                      className="group relative px-8 py-4 bg-gradient-to-r from-purple-500 via-pink-600 to-rose-600 rounded-lg font-bold text-lg neon-glow text-white overflow-hidden transition-all duration-300 hover:scale-105 animate-gradient-flow"
                    >
                      <span className="relative z-10 flex items-center justify-center gap-2">
                        Start Building Workflow
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                      </span>
                    </button>
                    <button
                      onClick={() => document.getElementById('canvas-examples')?.scrollIntoView({ behavior: 'smooth' })}
                      className="px-8 py-4 border-2 border-pink-500/50 hover:border-pink-400 rounded-lg font-semibold transition-all duration-300 hover:bg-pink-500/10 flex items-center justify-center gap-2 backdrop-blur-sm"
                    >
                      <Play className="w-5 h-5" />
                      View Examples
                    </button>
                  </div>

                  {/* Trust badges */}
                  <div className="flex flex-wrap gap-6 text-sm text-gray-400">
                    {['No coding required', 'Drag & drop interface'].map((badge, i) => (
                      <div key={i} className="flex items-center gap-2 animate-bounce-in" style={{ animationDelay: `${i * 0.1}s` }}>
                        <Check className="w-4 h-4 text-emerald-400" />
                        <span>{badge}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right: Canvas Demo (Placeholder) */}
                <div className="relative animate-float">
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-pink-500/20 border border-pink-500/30 group">
                    {/* Canvas Workflow Image */}
                    <img
                      src="https://data.vissynth.com/image/9Z2O1Fwq3BMCpPGs/EuVneyJBmQj9ME1R/24_1768980689_canvas_poster.jpg"
                      alt="Canvas Workflow Editor"
                      className="w-full h-[400px] object-contain rounded-3xl transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Carousel Controls */}
          <div className="flex justify-center gap-4 mt-12">
            <button
              onClick={() => setCurrentSlide(0)}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${currentSlide === 0 ? 'bg-white text-purple-600 shadow-lg' : 'bg-white/10 text-gray-400 hover:bg-white/20'}`}
              aria-label="Go to Video Creation slide"
            >
              <Video className="w-6 h-6" />
            </button>
            <button
              onClick={() => setCurrentSlide(1)}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${currentSlide === 1 ? 'bg-white text-purple-600 shadow-lg' : 'bg-white/10 text-gray-400 hover:bg-white/20'}`}
              aria-label="Go to Canvas Workflow slide"
            >
              <Grid className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation Arrows */}
          <div className="absolute top-1/2 left-[-40px] right-[-40px] -translate-y-1/2 flex justify-between pointer-events-none">
            <button
              onClick={() => setCurrentSlide(prev => prev === 0 ? 1 : 0)}
              className="pointer-events-auto w-12 h-12 rounded-full bg-white/10 backdrop-blur-lg border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => setCurrentSlide(prev => prev === 1 ? 0 : 1)}
              className="pointer-events-auto w-12 h-12 rounded-full bg-white/10 backdrop-blur-lg border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300"
              aria-label="Next slide"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      < section className="py-16 relative" >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md mb-6">
              <Flame className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-purple-300 font-medium">Powerful Features</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-black mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600">
                Explore Core Features
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Discover VisSynth's powerful features to meet all your creative needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.id}
                className="group relative p-8 rounded-2xl border border-gray-700/50 hover:border-gray-600 transition-all duration-500 hover:scale-105 overflow-hidden"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Gradient background on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>

                {/* Icon */}
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 relative overflow-hidden`}>
                  <div className="absolute inset-0 bg-black/20"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
                  <feature.icon className="w-8 h-8 text-white relative z-10" />
                  <div className={`absolute inset-0 ${feature.glow} blur-md`}></div>
                </div>

                <h3 className="text-2xl font-bold mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-cyan-400 group-hover:to-purple-400 transition-all">
                  {feature.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section >

      {/* Example Videos Section */}
      < section id="example-videos" className="py-16 relative overflow-hidden" >
        {/* Background effects */}
        < div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-purple-600/15 to-pink-600/15 rounded-full blur-3xl" ></div >
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-br from-blue-600/15 to-cyan-600/15 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md mb-6">
              <Sparkles className="w-4 h-4 text-pink-400 animate-twinkle" />
              <span className="text-sm text-pink-300 font-medium">Instant Reel Example Videos</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-black mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-purple-500 to-indigo-500">
                See What You Can Create
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Explore AI-generated videos across different categories
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {exampleVideos.map((example, index) => (
              <div
                key={index}
                className="group relative aspect-[9/16] rounded-2xl overflow-hidden cursor-pointer transform hover:scale-105 transition-all duration-500 animate-slide-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Video */}
                <video
                  src={example.videoUrl}
                  poster={example.posterUrl}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loop
                  playsInline
                  preload="metadata"
                  aria-label={`${example.title} - ${example.category} video demo`}
                  onMouseEnter={(e) => {
                    const video = e.currentTarget;
                    video.play().catch(err => {
                      console.log('Video play error:', err);
                    });
                  }}
                  onMouseLeave={(e) => e.currentTarget.pause()}
                  onPlay={() => {
                    setPlayingVideos(prev => new Set(prev).add(index));
                  }}
                  onPause={() => {
                    setPlayingVideos(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(index);
                      return newSet;
                    });
                  }}
                />

                {/* Gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${example.gradient} opacity-0 group-hover:opacity-30 transition-opacity duration-500 pointer-events-none`}></div>

                {/* Bottom overlay - always visible */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent flex flex-col items-end justify-end p-4 pointer-events-none">
                  <h3 className="text-sm font-bold text-white text-center">{example.title}</h3>
                </div>

                {/* Category badge */}
                <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-black/40 backdrop-blur-md border border-white/10 text-xs font-medium pointer-events-none">
                  {example.category}
                </div>

                {/* Play icon on hover - only show when paused */}
                <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 bg-black/40 pointer-events-none ${playingVideos.has(index) ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'}`}>
                  <Play className="w-16 h-16 text-white" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section >

      {/* Canvas Workflow Examples Section */}
      < section id="canvas-examples" className="py-16 relative overflow-hidden bg-gray-950" >
        {/* Background effects */}
        < div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-purple-600/15 to-pink-600/15 rounded-full blur-3xl" ></div >
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-br from-blue-600/15 to-cyan-600/15 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md mb-6">
              <Grid className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-purple-300 font-medium">Flow Canvas Workflow Examples</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-black mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-500 to-rose-500">
                Build Complex AI Workflows
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Explore node-based workflow examples for different use cases
            </p>
          </div>

          {/* Canvas workflow examples */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Example 1: Text to Video Workflow */}
            <div className="group relative p-6 rounded-2xl border border-purple-500/30 hover:border-purple-400 transition-all duration-500 hover:scale-105 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-rose-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <div className="aspect-[4/3] rounded-xl mb-4 overflow-hidden border border-purple-500/30">
                <img
                  src="https://data.vissynth.com/image/7DguxtMktvEq51mY/dHagPJEs77GQhVGS/24_1768982312_15.57.33.jpg"
                  alt="Text to Video Workflow"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>

              <h3 className="text-xl font-bold mb-3 text-white">Text to Video Workflow</h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                Create a complete text-to-video pipeline with AI-powered image generation and video synthesis.
              </p>
            </div>

            {/* Example 2: Image Generation Workflow */}
            <div className="group relative p-6 rounded-2xl border border-purple-500/30 hover:border-purple-400 transition-all duration-500 hover:scale-105 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-rose-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <div className="aspect-[4/3] rounded-xl mb-4 overflow-hidden border border-purple-500/30">
                <img
                  src="https://data.vissynth.com/image/dH4GP5QS7LmoTXW0/eSLHUgP1A67fCar5/24_1768981773_15.47.52.jpg"
                  alt="Image Generation Workflow"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>

              <h3 className="text-xl font-bold mb-3 text-white">Image Generation Workflow</h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                Build a multi-step image generation pipeline with style transfer and enhancement.
              </p>
            </div>

            {/* Example 3: Multi-modal AI Workflow */}
            <div className="group relative p-6 rounded-2xl border border-purple-500/30 hover:border-purple-400 transition-all duration-500 hover:scale-105 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-rose-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <div className="aspect-[4/3] rounded-xl mb-4 overflow-hidden border border-purple-500/30">
                <img
                  src="https://data.vissynth.com/image/zBsOfTeU79ss55q8/N5IkDFEWDFCMp9uK/24_1768982158_canvas_example3.jpg"
                  alt="Multi-modal AI Workflow"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>

              <h3 className="text-xl font-bold mb-3 text-white">Multi-modal AI Workflow</h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                Combine text, image, and video inputs to create powerful multi-modal AI pipelines.
              </p>
            </div>
          </div>
        </div>
      </section >

      {/* How It Works */}
      < section className="py-16 relative" >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md mb-6">
              <Rocket className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-emerald-300 font-medium">Simple Process</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-black mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-500 to-cyan-500">
                Create in 4 Simple Steps
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              From idea to finished creation in minutes
            </p>
          </div>

          {/* Process Tabs */}
          <div className="mb-12">
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <button
                onClick={() => setActiveProcessTab('video')}
                className={`px-6 py-3 rounded-lg font-bold text-white shadow-lg hover:shadow-xl transition-all duration-300 ${activeProcessTab === 'video' ? 'bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600' : 'bg-gray-800 border border-gray-700 hover:bg-gray-700'}`}
              >
                Video Creation Process
              </button>
              <button
                onClick={() => setActiveProcessTab('canvas')}
                className={`px-6 py-3 rounded-lg font-bold text-white shadow-lg hover:shadow-xl transition-all duration-300 ${activeProcessTab === 'canvas' ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500' : 'bg-gray-800 border border-gray-700 hover:bg-gray-700'}`}
              >
                Canvas Workflow Process
              </button>
            </div>
          </div>

          {/* Horizontal 4 Steps Layout */}
          <div className="relative">
            {/* Continuous line connecting all steps */}
            <div className={`hidden lg:block absolute top-8 left-0 right-0 h-0.5 ${activeProcessTab === 'video' ? 'bg-gradient-to-r from-blue-500/50 via-purple-500/50 via-pink-500/50 to-cyan-500/50' : 'bg-gradient-to-r from-purple-500/50 via-pink-500/50 via-rose-500/50 to-purple-500/50'}`}></div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
              {(activeProcessTab === 'video' ? steps : canvasSteps).map((step, index) => (
                <div key={index} className="relative text-center">
                  {/* Step Card */}
                  <div className="relative group">
                    {/* Number Badge - positioned on the line */}
                    <div className={`relative z-10 w-16 h-16 rounded-2xl bg-gray-900 border-2 flex items-center justify-center font-bold text-2xl mb-4 group-hover:scale-110 transition-transform duration-300 mx-auto shadow-lg ${activeProcessTab === 'video'
                      ? step.color === 'blue' ? 'border-blue-500/50 text-blue-400' :
                        step.color === 'purple' ? 'border-purple-500/50 text-purple-400' :
                          step.color === 'pink' ? 'border-pink-500/50 text-pink-400' :
                            'border-cyan-500/50 text-cyan-400'
                      : step.color === 'purple' ? 'border-purple-500/50 text-purple-400' :
                        step.color === 'pink' ? 'border-pink-500/50 text-pink-400' :
                          step.color === 'rose' ? 'border-rose-500/50 text-rose-400' :
                            'border-purple-500/50 text-purple-400'
                      }`}>
                      {step.number}
                    </div>

                    {/* Content */}
                    <div>
                      <h3 className={`text-2xl font-bold mb-3 ${activeProcessTab === 'video'
                        ? step.color === 'blue' ? 'text-blue-400' :
                          step.color === 'purple' ? 'text-purple-400' :
                            step.color === 'pink' ? 'text-pink-400' :
                              'text-cyan-400'
                        : step.color === 'purple' ? 'text-purple-400' :
                          step.color === 'pink' ? 'text-pink-400' :
                            step.color === 'rose' ? 'text-rose-400' :
                              'text-purple-400'
                        }`}>{step.title}</h3>
                      <p className="text-gray-400 text-base leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-16">
            <button
              onClick={() => navigate(activeProcessTab === 'video' ? '/production/video/input' : '/production/canvas')}
              className="px-12 py-5 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-lg font-bold text-xl hover:shadow-2xl hover:shadow-emerald-500/30 transition-all duration-300 transform hover:scale-105 text-white"
            >
              Start Creating Now
            </button>
          </div>
        </div>
      </section >

      {/* FAQ Section */}
      < section className="py-16 relative" >
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-orange-500 to-red-500">
                Frequently Asked Questions
              </span>
            </h2>
            <p className="text-xl text-gray-400">Learn more about VisSynth</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="glass-card rounded-xl overflow-hidden hover:scale-[1.02] transition-all duration-300"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
                >
                  <span className="font-bold text-lg">{faq.question}</span>
                  <ChevronDown
                    className={`w-6 h-6 transition-transform duration-300 ${activeFaq === index ? 'rotate-180' : ''}`}
                  />
                </button>
                {activeFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-400 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section >
      {/* Final CTA */}
      < section className="py-16 relative overflow-hidden" >
        {/* Background effects */}
        < div className="absolute inset-0" >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-cyan-600/30 via-purple-600/30 to-pink-600/30 rounded-full blur-3xl animate-pulse"></div>
        </div >

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="glass-card rounded-3xl p-12 border-2 border-cyan-500/20 relative overflow-hidden">
            {/* Animated gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10 animate-gradient-flow"></div>

            <div className="relative">
              <h2 className="text-4xl lg:text-6xl font-black mb-6">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 animate-gradient-flow">
                  Ready to start creating?
                </span>
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Join thousands of creators already using VisSynth
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/production/video/input')}
                  className="px-8 py-4 bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 rounded-lg font-bold text-lg text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                  Start Creating Video
                </button>
                <button
                  onClick={() => navigate('/production/canvas')}
                  className="px-8 py-4 bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 rounded-lg font-bold text-lg text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                  Start Building Workflow
                </button>
              </div>
              <p className="mt-6 text-sm text-gray-400">
                No credit card required • Free to use • Cancel anytime
              </p>
            </div>
          </div>
        </div>
      </section >

      {/* Footer */}
      < footer className="border-t border-gray-800 py-12 relative" >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center text-gray-400">
            <p className="mb-4">&copy; 2026 VisSynth. All rights reserved.</p>
            <div className="flex justify-center gap-6 text-sm">
              <a href="/privacy" className="hover:text-cyan-400 transition-colors">Privacy Policy</a>
              <a href="/terms" className="hover:text-cyan-400 transition-colors">Terms of Service</a>
              <a href="/feedback" className="hover:text-cyan-400 transition-colors">Contact Us</a>
            </div>
          </div>
        </div>
      </footer >
    </div >
  )
}

export default StartPage